import type { ComponentProps, ReactNode } from 'react';

import { FormLabel } from '@playbook/atoms/form-label';
import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type TextAreaSize = Extract<Size, 'sm' | 'md'>;

const DefaultTextAreaSize: TextAreaSize = 'md' as const;

export type TextAreaStatus = 'error' | 'success';

/** Figma draws the field three lines tall before it is dragged. */
const DefaultTextAreaRows: number = 3 as const;

type TextAreaEventHandlers = {
	[K in keyof ComponentProps<'textarea'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'textarea'>[K];
};

interface TextAreaDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TextAreaProperties
	extends TextAreaEventHandlers, TextAreaDataAttributes {
	size?: TextAreaSize;
	status?: TextAreaStatus;
	disabled?: boolean;
	required?: boolean;
	readOnly?: boolean;
	optional?: boolean;

	/**
	 *	`name` is written to the element's `id` as well, so the header points at
	 *	it with the same string and the description and message ids hang off it.
	 */
	name: string;
	value?: string;
	defaultValue?: string;
	placeholder?: string;
	rows?: number;
	maxLength?: number;

	label?: string;
	description?: string;
	help?: string;
	message?: string;

	header?: ReactNode;
	inlineHeader?: boolean;

	footer?: ReactNode;
	inlineFooter?: boolean;
}

export function TextArea({
	size = DefaultTextAreaSize,
	status,
	disabled = false,
	required = false,
	readOnly = false,
	optional = false,

	name,
	value,
	defaultValue,
	placeholder,
	rows = DefaultTextAreaRows,
	maxLength,

	label,
	description,
	help,
	message,

	header,
	inlineHeader,
	footer,
	inlineFooter,

	...props
}: TextAreaProperties): ReactNode {
	const className: string = cn(css.field, css[size], status && css[status]);

	/**
	 *	What the field is described by, in reading order. The first id is the one
	 *	`atoms/form-label` gives its description; the second is the message below,
	 *	which is the only place a failure is explained in words.
	 */
	const describedBy: string | undefined =
		[description && `${name}-description`, message && `${name}-message`]
			.filter(Boolean)
			.join(' ') || undefined;

	return (
		<div className={className}>
			{label && (
				<FormLabel
					size={size}
					optional={optional}
					required={required}
					htmlFor={name}
					label={label}
					description={description}
					help={help}
				/>
			)}
			<div
				data-size={size}
				data-status={status}
				className={cn(css.textArea, css[size], status && css[status])}>
				{header && (
					<header className={css.header} data-inline={inlineHeader}>
						{header}
					</header>
				)}
				<textarea
					{...props}
					id={name}
					name={name}
					value={value}
					defaultValue={defaultValue}
					placeholder={placeholder}
					rows={rows}
					maxLength={maxLength}
					disabled={disabled}
					required={required}
					readOnly={readOnly}
					aria-describedby={describedBy}
					aria-invalid={status === 'error' || undefined}
					className={css.control}
				/>
				{footer && (
					<footer className={css.footer} data-inline={inlineFooter}>
						{footer}
					</footer>
				)}
			</div>
			{message && (
				<span id={`${name}-message`} className={css.message}>
					{message}
				</span>
			)}
		</div>
	);
}
